import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import { useSelector } from 'react-redux';
import axios from '../../api/axios';
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Button,
    Form,
    Modal,
    Badge,
    Spinner,
    Alert,
    InputGroup,
    Pagination
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
    Plus,
    Calendar,
    Clock,
    Image as ImageIcon,
    Bell,
    Shop,
    Trash2,
    Edit2,
    Eye,
    Check,
    X,
    Upload,
    Search,
    Filter
} from 'react-bootstrap-icons';

const CartScheduler = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const { access } = useSelector((state) => state.login);

    // States for notification data
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Pagination states
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        title: '',
        subTitle: 'User who Order is pending as Items are only kept in cart',
        startDate: new Date(),
        endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
        time: '',
        type: 'PUSH',
        days: '',
        outlet: '',
        image: null
    });

    const [formErrors, setFormErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterOutlet, setFilterOutlet] = useState('all');

    // Fetch notifications on component mount
    useEffect(() => {
        fetchNotifications();
    }, [pagination.page]);

    // Fetch notifications from API
    const fetchNotifications = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get('cartnotification-scheduler', {
                params: {
                    page: pagination.page,
                    limit: pagination.limit
                },
                headers: {
                    Authorization: `Bearer ${access}`
                }
            });

            if (response.data.status) {
                setNotifications(response.data.data);
                setPagination(response.data.pagination);
            } else {
                setError(response.data.message || 'Failed to fetch notifications');
            }
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError(err.response?.data?.message || 'Failed to fetch notifications');
        } finally {
            setLoading(false);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Handle date changes
    const handleDateChange = (date, field) => {
        setFormData(prev => ({
            ...prev,
            [field]: date
        }));
    };

    // Handle image upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                image: file
            }));

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!formData.title.trim()) errors.title = 'Title is required';
        if (!formData.subTitle.trim()) errors.subTitle = 'Subtitle is required';
        if (!formData.startDate) errors.startDate = 'Start date is required';
        if (!formData.endDate) errors.endDate = 'End date is required';
        if (!formData.time) errors.time = 'Time is required';
        if (!formData.type) errors.type = 'Type is required';
        if (!formData.image) errors.image = 'Image is required';

        // Validate time format (HH:mm)
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (formData.time && !timeRegex.test(formData.time)) {
            errors.time = 'Time must be in HH:mm format';
        }

        // End date should be after start date
        if (formData.startDate && formData.endDate) {
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            if (end <= start) {
                errors.endDate = 'End date must be after start date';
            }
        }

        return errors;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        try {
            setSubmitting(true);

            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('subTitle', formData.subTitle);
            formDataToSend.append('startDate', formData.startDate.toISOString().split('T')[0]);
            formDataToSend.append('endDate', formData.endDate.toISOString().split('T')[0]);
            formDataToSend.append('time', formData.time);
            formDataToSend.append('type', formData.type);
            formDataToSend.append('image', formData.image);

            if (formData.days) formDataToSend.append('days', formData.days);
            if (formData.outlet) formDataToSend.append('outlet', formData.outlet);

            const response = await axios.post('/cartnotification-scheduler', formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${access}`
                }
            });

            if (response.data.status) {
                setSuccess('Notification scheduled successfully!');
                setShowCreateModal(false);
                resetForm();
                fetchNotifications();

                // Auto-hide success message after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.data.message || 'Failed to schedule notification');
            }
        } catch (err) {
            console.error('Error scheduling notification:', err);
            setError(err.response?.data?.message || 'Failed to schedule notification');
        } finally {
            setSubmitting(false);
        }
    };

    // Delete notification
    const handleDelete = async (id) => {
        try {
            const response = await axios.delete(`/cartnotification-scheduler/${id}`, {
                headers: {
                    Authorization: `Bearer ${access}`
                }
            });

            if (response.data.status) {
                setSuccess('Notification deleted successfully!');
                setShowDeleteModal(false);
                fetchNotifications();

                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.data.message || 'Failed to delete notification');
            }
        } catch (err) {
            console.error('Error deleting notification:', err);
            setError(err.response?.data?.message || 'Failed to delete notification');
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            subTitle: 'User who Order is pending as Items are only kept in cart',
            startDate: new Date(),
            endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
            time: '',
            type: 'PUSH',
            days: '',
            outlet: '',
            image: null
        });
        setFormErrors({});
        setImagePreview(null);
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Filter notifications
    const filteredNotifications = notifications.filter(notification => {
        // Search filter
        if (searchTerm && !notification.title.toLowerCase().includes(searchTerm.toLowerCase())) {
            return false;
        }

        // Type filter
        if (filterType !== 'all' && notification.type !== filterType) {
            return false;
        }

        // Outlet filter
        if (filterOutlet !== 'all') {
            if (filterOutlet === 'withOutlet' && !notification.outlet) return false;
            if (filterOutlet === 'withoutOutlet' && notification.outlet) return false;
        }

        return true;
    });

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({
            ...prev,
            page
        }));
    };

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
                <Container fluid>
                    {/* Success and Error Messages */}
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Header Section */}
                    <Row className="mb-4">
                        <Col>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h1 className="h3 mb-2 fw-bold">
                                        <Bell className="me-2" /> Cart Notification Scheduler
                                    </h1>
                                    <p className="text-muted mb-0">
                                        Schedule push notifications for cart reminders
                                    </p>
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={() => setShowCreateModal(true)}
                                    className="d-flex align-items-center"
                                >
                                    <Plus size={20} className="me-2" /> Schedule Notification
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Stats Cards */}
                    <Row className="mb-4">
                        <Col md={3} sm={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-muted mb-1">Total Scheduled</h6>
                                            <h3 className="mb-0">{pagination.total}</h3>
                                        </div>
                                        <div className="bg-primary bg-opacity-10 p-3 rounded-circle">
                                            <Bell size={24} className="text-primary" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} sm={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-muted mb-1">Active</h6>
                                            <h3 className="mb-0">
                                                {notifications.filter(n => new Date(n.endDate) >= new Date()).length}
                                            </h3>
                                        </div>
                                        <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                                            <Clock size={24} className="text-success" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} sm={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-muted mb-1">With Outlet</h6>
                                            <h3 className="mb-0">
                                                {notifications.filter(n => n.outlet).length}
                                            </h3>
                                        </div>
                                        <div className="bg-info bg-opacity-10 p-3 rounded-circle">
                                            <Shop size={24} className="text-info" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} sm={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-muted mb-1">Current Page</h6>
                                            <h3 className="mb-0">{pagination.page}</h3>
                                        </div>
                                        <div className="bg-warning bg-opacity-10 p-3 rounded-circle">
                                            <Calendar size={24} className="text-warning" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filters Section */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={4}>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <Search />
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Search by title..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Select
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                    >
                                        <option value="all">All Types</option>
                                        <option value="PUSH">Push Notifications</option>
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Select
                                        value={filterOutlet}
                                        onChange={(e) => setFilterOutlet(e.target.value)}
                                    >
                                        <option value="all">All Outlets</option>
                                        <option value="withOutlet">With Outlet</option>
                                        <option value="withoutOutlet">Without Outlet</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2} className="d-flex justify-content-end">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={fetchNotifications}
                                        disabled={loading}
                                    >
                                        <Filter className="me-2" /> Refresh
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Notifications Table */}
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 mb-0">Loading notifications...</p>
                                </div>
                            ) : filteredNotifications.length === 0 ? (
                                <div className="text-center py-5">
                                    <Bell size={48} className="text-muted mb-3" />
                                    <h5 className="text-muted">No notifications found</h5>
                                    <p className="text-muted mb-0">
                                        {searchTerm ? 'Try adjusting your search' : 'Schedule your first notification'}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <Table hover className="mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th width="60">#</th>
                                                    <th>Notification</th>
                                                    <th width="120">Type</th>
                                                    <th width="150">Schedule</th>
                                                    <th width="100">Outlet</th>
                                                    <th width="100">Days</th>
                                                    <th width="120">Status</th>
                                                    <th width="120" className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredNotifications.map((notification, index) => {
                                                    const isActive = new Date(notification.endDate) >= new Date();
                                                    const hasOutlet = !!notification.outlet;

                                                    return (
                                                        <tr key={notification._id}>
                                                            <td className="text-muted">
                                                                {(pagination.page - 1) * pagination.limit + index + 1}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <div className="me-3">
                                                                        <div className="bg-light rounded" style={{ width: '40px', height: '40px', overflow: 'hidden' }}>
                                                                            {notification.image ? (
                                                                                <img
                                                                                    src={`${process.env.REACT_APP_API_URL}/uploads/${notification.image}`}
                                                                                    alt={notification.title}
                                                                                    className="img-fluid"
                                                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                />
                                                                            ) : (
                                                                                <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                                                                                    <ImageIcon size={20} className="text-muted" />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <h6 className="mb-1">{notification.title}</h6>
                                                                        <p className="text-muted small mb-0">
                                                                            {notification.subTitle}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Badge bg="info" className="text-uppercase">
                                                                    {notification.type}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    <div className="d-flex align-items-center">
                                                                        <Calendar size={12} className="me-1" />
                                                                        {formatDate(notification.startDate)}
                                                                    </div>
                                                                    <div className="d-flex align-items-center mt-1">
                                                                        <Clock size={12} className="me-1" />
                                                                        {notification.time}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {hasOutlet ? (
                                                                    <Badge bg="success">
                                                                        <Shop size={12} className="me-1" /> Outlet
                                                                    </Badge>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {notification.days ? (
                                                                    <Badge bg="warning">
                                                                        {notification.days} day(s)
                                                                    </Badge>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Badge bg={isActive ? "success" : "secondary"}>
                                                                    {isActive ? 'Active' : 'Expired'}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    variant="outline-info"
                                                                    size="sm"
                                                                    className="me-1"
                                                                    onClick={() => {
                                                                        setSelectedNotification(notification);
                                                                        setShowViewModal(true);
                                                                    }}
                                                                >
                                                                    <Eye size={14} />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setSelectedNotification(notification);
                                                                        setShowDeleteModal(true);
                                                                    }}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination */}
                                    {pagination.totalPages > 1 && (
                                        <div className="d-flex justify-content-center py-3">
                                            <Pagination>
                                                <Pagination.Prev
                                                    onClick={() => handlePageChange(pagination.page - 1)}
                                                    disabled={pagination.page === 1}
                                                />
                                                {[...Array(pagination.totalPages).keys()].map(num => (
                                                    <Pagination.Item
                                                        key={num + 1}
                                                        active={num + 1 === pagination.page}
                                                        onClick={() => handlePageChange(num + 1)}
                                                    >
                                                        {num + 1}
                                                    </Pagination.Item>
                                                ))}
                                                <Pagination.Next
                                                    onClick={() => handlePageChange(pagination.page + 1)}
                                                    disabled={pagination.page === pagination.totalPages}
                                                />
                                            </Pagination>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Container>
            </div>

            {/* Create Notification Modal */}
            <Modal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Bell className="me-2" /> Schedule New Notification
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group controlId="formTitle">
                                    <Form.Label className="fw-semibold">
                                        Title <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter notification title"
                                        isInvalid={!!formErrors.title}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.title}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="formSubTitle">
                                    <Form.Label className="fw-semibold">
                                        Subtitle <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="subTitle"
                                        value={formData.subTitle}
                                        onChange={handleInputChange}
                                        placeholder="Enter notification subtitle"
                                        isInvalid={!!formErrors.subTitle}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.subTitle}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group controlId="formStartDate">
                                    <Form.Label className="fw-semibold">
                                        Start Date <span className="text-danger">*</span>
                                    </Form.Label>
                                    <DatePicker
                                        selected={formData.startDate}
                                        onChange={(date) => handleDateChange(date, 'startDate')}
                                        dateFormat="yyyy-MM-dd"
                                        className="form-control"
                                        wrapperClassName="w-100"
                                    />
                                    {formErrors.startDate && (
                                        <div className="text-danger small mt-1">{formErrors.startDate}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group controlId="formEndDate">
                                    <Form.Label className="fw-semibold">
                                        End Date <span className="text-danger">*</span>
                                    </Form.Label>
                                    <DatePicker
                                        selected={formData.endDate}
                                        onChange={(date) => handleDateChange(date, 'endDate')}
                                        dateFormat="yyyy-MM-dd"
                                        className="form-control"
                                        wrapperClassName="w-100"
                                    />
                                    {formErrors.endDate && (
                                        <div className="text-danger small mt-1">{formErrors.endDate}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group controlId="formTime">
                                    <Form.Label className="fw-semibold">
                                        Time (HH:mm) <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="time"
                                        value={formData.time}
                                        onChange={handleInputChange}
                                        placeholder="HH:mm"
                                        isInvalid={!!formErrors.time}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.time}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="formType">
                                    <Form.Label className="fw-semibold">
                                        Type <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        isInvalid={!!formErrors.type}
                                    >
                                        <option value="PUSH">Push Notification</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.type}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="formDays">
                                    <Form.Label className="fw-semibold">Days</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="days"
                                        value={formData.days}
                                        onChange={handleInputChange}
                                        placeholder="Enter number of days"
                                        min="1"
                                    />
                                    <Form.Text className="text-muted">
                                        Optional: Number of days for notification repetition
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="formOutlet">
                                    <Form.Label className="fw-semibold">Outlet ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="outlet"
                                        value={formData.outlet}
                                        onChange={handleInputChange}
                                        placeholder="Enter outlet ID (optional)"
                                    />
                                    <Form.Text className="text-muted">
                                        Optional: Specific outlet for notification
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="formImage">
                                    <Form.Label className="fw-semibold">
                                        Image <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div className="border rounded p-3 text-center">
                                        {imagePreview ? (
                                            <div className="mb-3">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="img-fluid rounded"
                                                    style={{ maxHeight: '150px' }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="text-muted mb-3">
                                                <ImageIcon size={48} />
                                                <p className="mt-2 mb-0">No image selected</p>
                                            </div>
                                        )}
                                        <Form.Control
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            isInvalid={!!formErrors.image}
                                            className="d-none"
                                            id="imageUpload"
                                        />
                                        <Form.Label
                                            htmlFor="imageUpload"
                                            className="btn btn-outline-primary w-100"
                                        >
                                            <Upload className="me-2" /> Choose Image
                                        </Form.Label>
                                        {formErrors.image && (
                                            <div className="text-danger small mt-2">{formErrors.image}</div>
                                        )}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowCreateModal(false);
                                resetForm();
                            }}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Scheduling...
                                </>
                            ) : (
                                <>
                                    <Check className="me-2" /> Schedule Notification
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* View Notification Modal */}
            <Modal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                size="md"
                centered
            >
                {selectedNotification && (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>Notification Details</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="text-center mb-4">
                                {selectedNotification.image ? (
                                    <img
                                        src={`${process.env.REACT_APP_API_URL}/uploads/${selectedNotification.image}`}
                                        alt={selectedNotification.title}
                                        className="img-fluid rounded"
                                        style={{ maxHeight: '200px' }}
                                    />
                                ) : (
                                    <div className="bg-light rounded p-5">
                                        <ImageIcon size={48} className="text-muted" />
                                    </div>
                                )}
                            </div>

                            <div className="mb-3">
                                <h5>{selectedNotification.title}</h5>
                                <p className="text-muted">{selectedNotification.subTitle}</p>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <small className="text-muted d-block">Type</small>
                                    <Badge bg="info" className="mt-1">
                                        {selectedNotification.type}
                                    </Badge>
                                </div>
                                <div className="col-6">
                                    <small className="text-muted d-block">Status</small>
                                    <Badge bg={new Date(selectedNotification.endDate) >= new Date() ? "success" : "secondary"} className="mt-1">
                                        {new Date(selectedNotification.endDate) >= new Date() ? 'Active' : 'Expired'}
                                    </Badge>
                                </div>
                            </div>

                            <hr />

                            <div className="small">
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">Start Date:</span>
                                    <span>{formatDate(selectedNotification.startDate)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">End Date:</span>
                                    <span>{formatDate(selectedNotification.endDate)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">Time:</span>
                                    <span>{selectedNotification.time}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">Days:</span>
                                    <span>{selectedNotification.days || 'Not specified'}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted">Outlet:</span>
                                    <span>{selectedNotification.outlet ? 'Specific Outlet' : 'All Outlets'}</span>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <small className="text-muted me-auto">
                                Created: {new Date(selectedNotification.createdAt).toLocaleString()}
                            </small>
                            <Button
                                variant="secondary"
                                onClick={() => setShowViewModal(false)}
                            >
                                Close
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-3">
                        <div className="bg-danger bg-opacity-10 d-inline-flex p-3 rounded-circle mb-3">
                            <Trash2 size={24} className="text-danger" />
                        </div>
                        <h5>Are you sure?</h5>
                        <p className="text-muted">
                            This will permanently delete the notification "{selectedNotification?.title}".
                            This action cannot be undone.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => handleDelete(selectedNotification?._id)}
                    >
                        <Trash2 className="me-2" /> Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
};

export default CartScheduler;